// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { createStandaloneToast } from "@chakra-ui/react";
import { uniq } from "lodash";
import get from "lodash/get";
import moment from "moment";
import { i18n } from "next-i18next";
import {
  CHARGE_OTC_STATUS,
  CHARGE_SUBSCRIPTION_STATUS
} from "opsComponents/SubOrderDetail/constants";
import { v4 as uuidv4 } from "uuid";

import { filterActivePrices, getFilteredPriceType } from "components/PriceSelection/utils";
import { LOCAL_REGION_LIST, TAX_PERCENT } from "components/constants";
import {
  AcuityAppointmentModel,
  CouponDuration,
  EvalIds,
  ImageDto,
  Language,
  LocalRegionType,
  OrderDto,
  OrderStatus,
  PaymentProvider,
  PlatformType,
  ProductNameType,
  RegionType,
  StripeCouponModel,
  StripePriceLocaleKeys,
  StripePriceModel,
  StripePriceType,
  StripeProductModel
} from "graphql/generated";
import { Evaluations } from "src/configs/evaluations";
import { checkCountry } from "src/utils/checkCountry";

import { DATE_FORMAT, MMMM_DATE_FORMAT } from "./constants/Common";
import { COUNTRY_CODES, CURRENT_COUNTRY } from "./constants/Countries";
import { CONSULT_ONLY_PROD_UNIQNAME, CONSULT_ONLY_TREAMENTS } from "./constants/Evaluations";
import { OTC_ZOEY_PRODUCTS } from "./constants/Products";
import { URL_ORDER_BY_CONDITION } from "./constants/url.constants";
import { stripeCurrencyRatio } from "./utils/envConfig";

export const defaultClientId = "0000000000.0000000000";

export const showSuccess = (message: string) => {
  const toast = createStandaloneToast();
  toast({
    title: message,
    status: "success",
    duration: 9000,
    isClosable: true
  });
};

export const showError = (message: string) => {
  const toast = createStandaloneToast();
  toast({
    title: message,
    status: "error",
    duration: 9000,
    isClosable: true
  });
};

export const trimObject = (obj: any) =>
  JSON.parse(JSON.stringify(obj, (k, v) => (typeof v === "string" ? v.trim() : v)));

export const toPascalCase = (s) =>
  s.replace(/(\w)(\w*)/g, (g0, g1, g2) => g1.toUpperCase() + g2.toLowerCase());

export const getCountry = () =>
  process.env.NEXT_PUBLIC_COUNTRY === COUNTRY_CODES.SG ? COUNTRY_CODES.SG : COUNTRY_CODES.HK;

export const getStripeProductCollection = () =>
  getCountry() === COUNTRY_CODES.SG ? "stripeProducts" : "stripeProductsHK";

export const getUtmSource = () => {
  const referrer = localStorage.referrer || "";
  const { utm_source } = localStorage;
  const gclid = localStorage.gclid || "";
  const fbclid = localStorage.fbclid || "";

  if (!utm_source) {
    if (referrer.includes("google")) return "Organic Search";
    if (referrer.includes("fb")) return "Organic Facebook";
    if (referrer.includes("instagram")) return "Organic Instagram";
    if (referrer === "") return "Direct";
  } else {
    if (utm_source.includes("google") || utm_source.includes("gdn") || gclid) return "Google Ads";
    if (utm_source.includes("bing")) return "Bing";
    if (utm_source.includes("taboo")) return "Taboola";
    if (utm_source.includes("outb")) return "Outbrain";
    if (utm_source.includes("programmatic-display")) return "Programmatic";
    if (
      utm_source.includes(" ") ||
      utm_source.includes("sendgrid") ||
      utm_source.includes("email")
    ) {
      return "Email";
    }
    if (
      utm_source.includes("fb") ||
      utm_source.includes("facebook") ||
      utm_source.includes("ig") ||
      utm_source.includes("an") ||
      utm_source.includes("msg") ||
      utm_source.includes("{{site") ||
      fbclid
    ) {
      return "Facebook Ads";
    }
  }

  if (referrer.includes("taboo")) return "Taboola";
  if (referrer.includes("outb")) return "Outbrain";

  if (
    referrer.includes("secure-ds") ||
    referrer.includes("bs.serving") ||
    referrer.includes("android-app://")
  ) {
    return "Programmatic";
  }

  return "Others";
};

/**
 * Generate Authorization object of request header
 * @param token auth token
 * @returns Authorization object
 */
export const generateAuthorizationHeader = (token, locale? = "en") => ({
  Authorization: `Bearer ${token || ""}`,
  "accept-language": locale
});

/**
 * Generate uuid for new be
 * @returns uuid
 */
export const uuidGen = () => uuidv4();

/**
 * convert dob to age
 * @param dob string dob
 * @returns age of dob
 */
export const getAge = (dob: string) => (dob ? moment().diff(moment(dob), "year") : null);

export const getAcuityData = (acuity: AcuityAppointmentModel, locale = "en-US") => {
  const acuityDate = moment(acuity?.datetime).locale(locale).format(MMMM_DATE_FORMAT);
  const acuityTime = moment(acuity?.datetime).locale(locale).format("dddd hh:mmA");
  const isFuture = moment(acuity?.datetime).isAfter(new Date(), "second");
  const doctorName = acuity?.calendar;

  return { acuityDate, acuityTime, doctorName, isFuture };
};
/**
 * Convert string to enum key
 * @param str Zoey x LELO: SONA™ 2 Bundle%
 * @returns ZoeyXLeloSona2Bundle
 */
export const convertStringToEnumKey = (str: string) => {
  if (!str) {
    return "";
  }
  return str
    .toLowerCase()
    .replace(/[\s+\-.,:™%)(]+/g, " ")
    .replace(/(?:^\w|\b\w)/g, (word) => word.toUpperCase())
    .replace(/\s+/g, "");
};

export const checkPatientAge = (value: string) => {
  if (!value) {
    return false;
  }

  const age = moment().diff(moment(value, DATE_FORMAT), "years");
  if (age < 18 || age > 99) {
    return false;
  }
  return true;
};

export const checkIsFutureDate = (value: string) => {
  if (!value) {
    return false;
  }

  return moment(value).isAfter(new Date(), "second");
};

export const formatNumberToStars = (props) => {
  // convert evaluation title into star
  let style = "";
  for (let i = 0; i <= props; i += 1) {
    style += "*";
  }
  return style;
};

/**
 * Get image url from the image model
 * @param image image model
 * @param platform current platform
 * @param isVerticle is verticle or wide
 * @returns image url string
 */
export const getImageUrlFromImageModel = (
  image: Omit<ImageDto, "__typename">,
  platform: PlatformType,
  isVerticle: boolean
): string => {
  if (!image) return "";
  switch (platform) {
    case PlatformType.Noah:
      return isVerticle ? image?.noahVertical : image?.noahWide;
    case PlatformType.Zoey:
      return isVerticle ? image?.zoeyVertical : image?.zoeyWide;
    default:
      return image?.[`${platform}${isVerticle ? "Verticle" : "Wide"}`];
  }
};

export const isEDBundleProduct = (product?: StripeProductModel): boolean => {
  return (
    [
      ProductNameType.EdBundleFirstTimeTrial,
      ProductNameType.EdChristmasBundleFirstTimeTrial
    ] as string[]
  ).includes(product?.uniqName);
};

export const isLuckyBundleProduct = (product?: StripeProductModel): boolean => {
  return ([ProductNameType.LuckyBundle] as string[]).includes(product?.uniqName);
};

function daysIntoYear(date) {
  return (
    (Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) -
      Date.UTC(date.getFullYear(), 0, 0)) /
    24 /
    60 /
    60 /
    1000
  );
}

// Get date of year to display on the chart
export const getDateOfYear = (date) => {
  const day = daysIntoYear(date);
  const totalDaysOfYear = moment().isLeapYear() ? 366 : 365;

  // 12 means 12 months of the year
  return (day * 12) / totalDaysOfYear;
};

export const formatTimeByLanguage = (unixTimestamp) =>
  i18n.language === Language.En
    ? moment.unix(unixTimestamp).locale("en-Us").format("DD MMMM YYYY")
    : i18n.language === Language.Zh
    ? moment.unix(unixTimestamp).locale("zh_cn").format("LL")
    : moment.unix(unixTimestamp).locale("ja").format("LL");

export const resetBodyScrolling = () => {
  document.body.classList.remove("custom-body");
  document.body.classList.add("reset-body");

  document.body.style.removeProperty("overflow");
  document.body.style.removeProperty("position");
  document.body.style.removeProperty("top");
  document.body.style.removeProperty("width");
};

export const disableBodyScrolling = () => {
  document.body.classList.remove("reset-body");
  document.body.classList.add("custom-body");

  document.body.style.overflow = "hidden";
  document.body.style.position = "fixed";
  document.body.style.top = `0px`;
  document.body.style.width = "100%";
};

export const hasDuplicates = (elements: string[]) => new Set(elements).size !== elements.length;

export const checkIsOrderValid = (values) => Object.values(values).every((x) => x !== undefined);

export const checkIsOnHold = (status: string, subscription: any) => {
  if (status !== "active") {
    return false;
  }
  return subscription?.pause_collection?.behavior === "keep_as_draft";
};

/**
 * Robustly copy `text` to the clipboard.
 *
 * `navigator.clipboard` is undefined in insecure contexts — notably http://
 * localhost dev/ops, and some embedded webviews — so a naive `navigator.clipboard.writeText`
 * either throws or silently no-ops, which previously surfaced a "Cannot copy" toast.
 * We try the async Clipboard API first, then fall back to a hidden-textarea +
 * `document.execCommand("copy")`, and only toast failure if BOTH paths fail.
 */
const copyViaExecCommand = (text: string): boolean => {
  if (typeof document === "undefined") return false;
  const textarea = document.createElement("textarea");
  textarea.value = text;
  // Keep it out of the viewport / from stealing focus or scroll.
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "-9999px";
  textarea.style.left = "-9999px";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  let copied = false;
  try {
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    copied = document.execCommand("copy");
  } catch {
    copied = false;
  } finally {
    document.body.removeChild(textarea);
  }
  return copied;
};

export const handleCopyText = (text: string, toast: any, e?: any) => {
  e?.stopPropagation?.();

  const onSuccess = () =>
    toast({
      title: `Copied`,
      duration: 3000,
      isClosable: true,
      status: "success"
    });

  const onFailure = () =>
    toast({
      title: `Cannot copy`,
      duration: 3000,
      isClosable: true,
      status: "error"
    });

  if (navigator?.clipboard?.writeText) {
    // Wrap in Promise.resolve so a non-thenable return (older polyfills / test mocks)
    // is handled the same as the real async API.
    Promise.resolve(navigator.clipboard.writeText(text)).then(onSuccess, () => {
      // Async Clipboard API rejected (blocked permission / insecure context) — fall back.
      if (copyViaExecCommand(text)) onSuccess();
      else onFailure();
    });
    return;
  }

  if (copyViaExecCommand(text)) onSuccess();
  else onFailure();
};

export const getGraphQLErrorMsg = (error: Error) => {
  let message = "";
  const e = JSON.stringify(error, undefined, 2);
  try {
    message = get(JSON.parse(e), "response.errors[0].message");
  } catch {
    message = "Server error";
  }
  return message;
};

export const isBase64 = async (str) => {
  const image = new Image();
  image.src = str;
  return new Promise((resolve) => {
    image.onload = function () {
      if (image.height === 0 || image.width === 0) {
        resolve(false);
        return;
      }
      resolve(true);
    };
    image.onerror = () => {
      resolve(false);
    };
  });
};

export const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = array[i];
    array[i] = array[j];
    array[j] = temp;
  }
  return array;
};

export const removeHyphen = (str) => {
  return str.replace(/-/g, "");
};

export const mappingLocalRegion = (postal: string): LocalRegionType => {
  const code = postal?.slice(0, 2);
  return LOCAL_REGION_LIST[CURRENT_COUNTRY][code];
};

export const getPriceLabel = (t, priceTrans, price): string => {
  return price?.priceDisplayId
    ? `${priceTrans?.[StripePriceLocaleKeys.FrequencyQuantityDisplay]} shipped every ${
        price?.priceMetadata?.frequency
      } months${price?.priceMetadata?.isArchived === "true" ? " (old price)" : ""}`
    : t("form.message.invalid-price");
};

export const getUnitAmountByRegion = (unitAmount: number): number => {
  if (!unitAmount && unitAmount !== 0) {
    return null;
  }

  return unitAmount / stripeCurrencyRatio();
};

export const getCurrencyIcon = () => {
  if (checkCountry([RegionType.Jp])) {
    return "¥";
  }
  return "$";
};

export const addCurrencyPrefix = (value: string) => {
  if (value === "") {
    return "";
  }
  if (value.startsWith("Total")) {
    return value;
  }
  if (checkCountry([RegionType.Jp])) {
    return `¥${value}`;
  }
  return `$${value}`;
};

export const extractNumberFromText = (text: string): number => {
  const regex = /[^0-9\.-]+/g;
  const number = +text?.replace(regex, "");

  return number;
};

export function jsonBooleanTryParse(str: string) {
  let result;
  try {
    result = JSON.parse(str.toString().toLowerCase());
    return result;
  } catch (e) {
    return result;
  }
}

export const toTitleCase = (str: string): string => {
  return str?.replace(/(^|\s)\S/g, (t) => t.toUpperCase());
};

// use for server side when hook can't be used
export const addTransSuffixCountry = (value: string) => {
  if (CURRENT_COUNTRY === RegionType.Sg) {
    return value;
  }

  return `${value}_${CURRENT_COUNTRY}`;
};

/**
 * get translate key for case  JP different from HK, SG  - JP have different EN version
 * Example: embed video only in JP
 * in SG, HK en will: header
 * JP en should be: header_jp
 * JP ja: header (only ja)
 *
 * @param locale
 * @param value translate key
 * @returns translate key for JP
 */

export const addTransSuffixJp = (locale) => (value: string) => {
  if (process.env.NEXT_PUBLIC_COUNTRY === RegionType.Jp && locale === "en") {
    return `${value}_${process.env.NEXT_PUBLIC_COUNTRY}`;
  }
  return value;
};

/**
 * get short explaination text like "SGD 120 / month"
 * @param price price's info
 * @param t must include ops_product
 * @returns
 */
export const getPriceShortExplainText = (price: StripePriceModel, t: any) => {
  const result = `
  ${price?.currency?.toUpperCase()} ${price?.unit_amount}${
    price?.recurring
      ? ` / ${t(`medication.${price?.recurring?.interval_count}-${price?.recurring?.interval}`)}`
      : ""
  }
`;
  return result;
};

/**
 * get price interval short explaination text like "monthly, every 3 month"
 * @param price price's info
 * @param t must include ops_product
 * @returns
 */
export const getPriceIntervalShortExplainText = (price: StripePriceModel, t: any) => {
  const result = `
  ${t(
    `interval-explaination.${
      price?.recurring
        ? `${price?.recurring?.interval_count}-${price?.recurring?.interval}`
        : "one-time"
    }`
  )}
`;
  return result;
};

/**
 * get short explaination text like "SGD 120 / month"
 * @param coupon coupon's info
 * @param t must include ops_coupon
 * @returns terms text
 */
export const getCouponTermsText = (coupon: StripeCouponModel, t: any) => {
  let result = "";
  // add text for discount value
  if (coupon?.amount_off > 0) {
    result += `${coupon?.currency?.toUpperCase()} ${coupon.amount_off} ${t("terms.off")}`;
  } else if (coupon?.percent_off > 0) {
    result += `${coupon.percent_off}% ${t("terms.off")}`;
  }

  // add text for duration value
  if (coupon?.duration === CouponDuration.Once) {
    result += ` ${t("terms.once")}`;
  } else if (coupon?.duration === CouponDuration.Forever) {
    result += ` ${t("terms.forever")}`;
  } else if (coupon?.duration === CouponDuration.Repeating && coupon?.duration_in_months > 0) {
    result += ` ${t("terms.repeating", { months: coupon?.duration_in_months })}`;
  }
  return result;
};

const isValidOtcStatus = (isOtcOrder: boolean, orderStatus: OrderStatus): boolean => {
  return isOtcOrder && !CHARGE_OTC_STATUS.includes(orderStatus);
};

const isValidSubStatus = (isOtcOrder: boolean, orderStatus: OrderStatus): boolean => {
  return !isOtcOrder && !CHARGE_SUBSCRIPTION_STATUS.includes(orderStatus);
};

export const checkDisableCharge = (
  isOtcOrder: boolean,
  orderStatus: OrderStatus,
  paymentProvider: PaymentProvider
): boolean => {
  return (
    isValidOtcStatus(isOtcOrder, orderStatus) ||
    isValidSubStatus(isOtcOrder, orderStatus) ||
    paymentProvider === PaymentProvider.Konbini
  );
};

export const checkIsConsultOnly = (
  evaluationFormId: string | EvalIds,
  orderProductUniqName: string | ProductNameType
): boolean => {
  const isConsultOnly =
    CONSULT_ONLY_TREAMENTS.includes(evaluationFormId as EvalIds) ||
    CONSULT_ONLY_PROD_UNIQNAME.includes(orderProductUniqName as ProductNameType);

  return isConsultOnly;
};

export const checkShouldChargeNow = (product: StripeProductModel): boolean => {
  if (
    product.metadata?.charge_now === "true" ||
    product.evaluationId?.noah === EvalIds.CovidTeleconsult
  ) {
    return true;
  }

  return false;
};

export const checkIsRxOneTime = (product: StripeProductModel): boolean => {
  if (!product.metadata?.requires_prescription) {
    return false;
  }

  const activePrices = filterActivePrices(product.prices);
  const priceType = getFilteredPriceType(activePrices);

  return priceType === StripePriceType.OneTime;
};

export const checkIsCountryHaveTextConsult = () => {
  return checkCountry([RegionType.Jp, RegionType.Hk]);
};

export const enableConsultTypeSelecting = (evaluationId: EvalIds): boolean => {
  return (
    [EvalIds.Ed, EvalIds.Pe, EvalIds.Hairloss].includes(evaluationId) &&
    checkIsCountryHaveTextConsult()
  );
};

export const checkIsFromZoeyOtcSub = (redirectPath: string): boolean => {
  if (!redirectPath) {
    return false;
  }

  if (!redirectPath.includes(URL_ORDER_BY_CONDITION)) {
    return false;
  }

  const redirectArr = redirectPath.split("/");
  const urlProduct = redirectArr?.[redirectArr.length - 1];

  if (OTC_ZOEY_PRODUCTS.some((prod) => urlProduct?.startsWith(prod))) {
    return true;
  }
};

export const checkIsSubscriptionPrice = (order: Partial<Pick<OrderDto, "price">>) => {
  return order?.price?.type === StripePriceType.Recurring;
};

export const initProductLink = (productName: string, fileName: string): string => {
  return `products/${CURRENT_COUNTRY}/${productName}/${fileName?.replace(/ /g, "-")}`;
};

export const roundToTwo = (num: number): number => {
  return Math.round(num * 100) / 100;
};

export const roundPrice = (price: number): number => {
  return checkCountry([RegionType.Jp]) ? Math.round(price) : roundToTwo(price);
};

export const getBasePrice = (price: number): number => {
  const basePrice = price / (1 + TAX_PERCENT[CURRENT_COUNTRY] / 100);

  return roundPrice(basePrice);
};

export const getTax = (price: number): number => {
  const tax = (getBasePrice(price) * TAX_PERCENT[CURRENT_COUNTRY]) / 100;

  return roundPrice(tax);
};

export const getTransactionFee = (price: number): number => {
  const transactionFee = price - getBasePrice(price) - getTax(price);
  return roundPrice(transactionFee);
};

export const getAllEvalsOfCurrentCountry = (): EvalIds[] => {
  const treatmentList = Evaluations[CURRENT_COUNTRY];
  const noahTreatment = treatmentList[PlatformType.Noah] || [];
  const zoeyTreatment = treatmentList[PlatformType.Zoey] || [];
  return uniq([...noahTreatment, ...zoeyTreatment].map((ele) => ele?.code)) as EvalIds[];
};
